import { SelectionModel } from '@angular/cdk/collections';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, contentChildren, effect, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSortModule, type Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';

import { NoDataPlaceholderComponent } from '../../placeholders/no-data-placeholder.component';
import { CdkVirtualScrollViewportWrapperComponent } from '../../scroll/cdk-virtual-scroll-viewport-wrapper.component';
import { LoadMoreDirective } from '../../scroll/load-more.directive';
import { SkeletonTableComponent } from '../../skeleton/skeleton-table.component';
import { MetricCellComponent } from '../metric-cell.component';
import { TableZoomComponent } from '../table-zoom.component';
import { DatagridCellDirective } from './datagrid-cell.directive';
import { type DatagridColumn } from './datagrid-column';

/** A consumer using `<ng-template avsCell="columnId">` must also import `DatagridCellDirective`
 * into its own `@Component({ imports: [...] })` - standalone directives don't activate
 * transitively through a child component's imports. */
@Component({
  host: { class: 'flex flex-col overflow-hidden' },
  imports: [
    CdkVirtualScrollViewportWrapperComponent,
    LoadMoreDirective,
    MatCheckbox,
    MatSortModule,
    MatTableModule,
    MetricCellComponent,
    NgTemplateOutlet,
    NoDataPlaceholderComponent,
    ScrollingModule,
    SkeletonTableComponent,
    TableZoomComponent,
  ],
  selector: 'avs-datagrid',
  templateUrl: './datagrid.component.html',
})
export class AvsDatagridComponent<TRow extends { id: string | number }> {
  rows = input.required<TRow[]>();
  columns = input.required<DatagridColumn<TRow>[]>();
  isLoading = input(false);
  hasMore = input(false);
  rowHeight = input(44);
  noDataText = input('No Data');

  alwaysShowTable = input(false);
  stickyColumns = input(true);
  selectable = input(false);
  selectAll = input(true);
  selected = input<TRow['id'][]>([]);
  rowClass = input<((row: TRow) => string | null) | null>(null);
  sort = input<Sort | null>(null);
  zoomControls = input(false);
  tableId = input<string | null>(null);

  sortChange = output<Sort>();
  loadMore = output<void>();
  selectionChanged = output<TRow['id'][]>();

  protected readonly zoom = signal<string | null>(null);

  protected readonly displayedColumnIds = computed(() => {
    const ids = this.columns()
      .filter(column => !column.hidden?.())
      .map(column => column.id);

    return this.selectable() ? ['select', ...ids] : ids;
  });

  protected readonly firstVisibleColumn = computed(() => this.columns().find(column => !column.hidden?.()));

  protected readonly selection = new SelectionModel<TRow['id']>(true, []);

  private readonly cellTemplates = computed(
    () => new Map(this.cellDirectives().map(directive => [directive.columnId(), directive.templateRef])),
  );

  private readonly cellDirectives = contentChildren(DatagridCellDirective);

  constructor() {
    effect(() => this.selection.select(...this.selected()));

    this.selection.changed
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.selectionChanged.emit(this.selection.selected));
  }

  protected cellTemplateFor(columnId: string) {
    return this.cellTemplates().get(columnId);
  }

  protected onLoadMore() {
    if (this.hasMore()) {
      this.loadMore.emit();
    }
  }

  protected isAllSelected() {
    return this.rows().length > 0 && this.rows().every(row => this.selection.isSelected(row.id));
  }

  protected toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();

      return;
    }

    this.selection.select(...this.rows().map(row => row.id));
  }
}
